import * as React from 'react';

import { enumDocumentType } from '@rematter/frontend-lib-api-client';
import { Button, Menu, MenuItem } from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useSetAtom } from 'jotai';
import {
  LucideDownload,
  LucideEye,
  LucideImage,
  LucideMoreVertical,
  LucidePrinter,
} from 'lucide-react';

import * as state from '../../_state/transactions.state';
import { usePrintWithBrowser } from '../../hooks/usePrintWithBrowser';
import { useTranslations } from '../../i18n/useTranslations';
import { InboundTransactionRecord } from '../../types/transaction';

import { TransactionPhotosDialog } from './TransactionPhotosDialog';
import { TransactionSidePanel } from './TransactionSidePanel';

type InboundTransactionActionMenuProps = {
  transaction: InboundTransactionRecord;
};

//
// ⚛️ ⚛️ ⚛️ INBOUND TRANSACTION ACTION MENU ⚛️ ⚛️ ⚛️
//
export const InboundTransactionActionMenu = ({
  transaction,
}: InboundTransactionActionMenuProps) => {
  const t = useTranslations();

  const [sidePanelOpen, setSidePanelOpen] = React.useState(false);
  const [photosDialogOpen, setPhotosDialogOpen] = React.useState(false);

  // Atoms
  const setSelectedTransactionId = useSetAtom(state.$selectedTransactionId);

  // Data hooks
  const { print, isPrinting } = usePrintWithBrowser();
  const toast = useToast();

  // Callbacks
  const handlePrintBol = React.useCallback(() => {
    print({
      documents: [
        { documentType: enumDocumentType.BillOfLading, loadId: transaction.id },
      ],
    });
    toast.info(t.transactionsView.actions.printingBillOfLading);
  }, [print, transaction.id, t, toast]);

  const handlePrintPo = React.useCallback(() => {
    print({
      documents: [
        {
          documentType: enumDocumentType.PurchaseOrder,
          loadId: transaction.id,
          purchaseOrderId: transaction.purchaseOrderId ?? undefined,
        },
      ],
    });
    toast.info(t.transactionsView.actions.printingPurchaseOrder);
  }, [print, transaction.id, transaction.purchaseOrderId, t, toast]);

  const handleDownloadBol = React.useCallback(async () => {
    await print({
      documents: [
        { documentType: enumDocumentType.BillOfLading, loadId: transaction.id },
      ],
      action: 'download',
    });
  }, [print, transaction.id]);

  const handleDownloadPo = React.useCallback(async () => {
    await print({
      documents: [
        {
          documentType: enumDocumentType.PurchaseOrder,
          loadId: transaction.id,
          purchaseOrderId: transaction.purchaseOrderId ?? undefined,
        },
      ],
      action: 'download',
    });
  }, [print, transaction.id, transaction.purchaseOrderId]);

  const handleOpenSidePanel = React.useCallback(() => {
    setSelectedTransactionId(transaction.id);
    setSidePanelOpen(true);
  }, [setSelectedTransactionId, transaction.id]);

  const handleOpenPhotosDialog = React.useCallback(() => {
    setSelectedTransactionId(transaction.id);
    setPhotosDialogOpen(true);
  }, [setSelectedTransactionId, transaction.id]);

  // Memos
  const menuItems = React.useMemo<MenuItem[]>(() => {
    const items: MenuItem[] = [
      {
        label: t.transactionsView.actions.printDocuments,
        isDisabled: true,
        className: 'text-xs font-semibold uppercase text-neutral/50',
      },
      {
        label: t.transactionsView.actions.billOfLading,
        icon: <LucidePrinter />,
        onAction: handlePrintBol,
        isDisabled: isPrinting,
      },
      ...(transaction.purchaseOrderId
        ? [
            {
              label: t.transactionsView.actions.purchaseOrder,
              icon: <LucidePrinter />,
              onAction: handlePrintPo,
              isDisabled: isPrinting,
            } satisfies MenuItem,
          ]
        : []),
      'separator' as const,
      {
        label: t.transactionsView.actions.downloadDocuments,
        isDisabled: true,
        className: 'text-xs font-semibold uppercase text-neutral/50',
      },
      {
        label: t.transactionsView.actions.billOfLading,
        icon: <LucideDownload />,
        onAction: handleDownloadBol,
      },
      ...(transaction.purchaseOrderId
        ? [
            {
              label: t.transactionsView.actions.purchaseOrder,
              icon: <LucideDownload />,
              onAction: handleDownloadPo,
            } satisfies MenuItem,
          ]
        : []),
      'separator' as const,
      {
        label: t.transactionsView.actions.ticketDetails,
        isDisabled: true,
        className: 'text-xs font-semibold uppercase text-neutral/50',
      },
      {
        label: t.transactionsView.actions.viewAdditionalDetails,
        icon: <LucideEye />,
        onAction: handleOpenSidePanel,
      },
      {
        label: t.transactionsView.actions.viewScaleTicketPhotos,
        icon: <LucideImage />,
        onAction: handleOpenPhotosDialog,
      },
    ];

    return items;
  }, [
    t,
    isPrinting,
    transaction.purchaseOrderId,
    handlePrintBol,
    handlePrintPo,
    handleDownloadBol,
    handleDownloadPo,
    handleOpenSidePanel,
    handleOpenPhotosDialog,
  ]);

  return (
    <div
      className="@InboundTransactionActionMenu flex justify-center"
      onClick={(e) => e.stopPropagation()}
    >
      <Menu
        trigger={
          <Button size="icon" icon={<LucideMoreVertical />} variant="outline" />
        }
        items={menuItems}
      />
      <TransactionSidePanel
        direction="inbound"
        documentLabel={String(transaction.documentNumber)}
        isOpen={sidePanelOpen}
        onOpenChange={setSidePanelOpen}
      />
      <TransactionPhotosDialog
        documentNumber={transaction.documentNumber}
        isOpen={photosDialogOpen}
        onOpenChange={setPhotosDialogOpen}
      />
    </div>
  );
};
